import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Lock, ShieldCheck, ArrowLeft } from 'lucide-react';
import { de } from '../lib/i18n/de';
import { statement } from '@tragfaehigkeit/core';
import { renderAndDownloadStatement } from '../lib/statement/renderDocx';
import { openPrintPreview } from '../lib/statement/printPdf';
import { buildDemoStatementInput } from '../lib/statement/demoFixture';
import { buildInputFromCase } from '../lib/statement/buildInputFromCase';
import { AssessmentEditor } from '../components/statement/AssessmentEditor';
import {
  checkAssessmentReadiness,
  selectAssessmentsForCase,
} from '../lib/statement/assessmentsAdapter';
import { useWizardStore } from '../lib/store/wizard';
import { useWizardSync } from '../lib/store/useWizardSync';
import { useAuthOrSession } from '../lib/authOrSession';
import { getCase } from '../lib/supabaseCases';
import { getOpenReviewForCase, requestReview } from '../lib/supabaseCaseReviews';
import { getFeatureFlag } from '../lib/supabaseFeatureFlags';
import {
  createStatementVersion,
  listStatementVersionsForCase,
} from '../lib/supabaseStatementVersions';
import { listMandantAccessForCase } from '../lib/supabaseInvitations';
import type { CaseReviewRow, CaseStatus, StatementVersionRow } from '../lib/supabaseTypes';
import { InviteMandantSection } from '../components/invitations/InviteMandantSection';
import { MandantenStatusBoard } from '../components/invitations/MandantenStatusBoard';
import { ClarificationsSection } from '../components/cockpit/ClarificationsSection';
import { CockpitStammdatenAndDocs } from '../components/cockpit/CockpitStammdatenAndDocs';
import { DsgvoSection } from '../components/cockpit/DsgvoSection';
import { BeraterDimensionReviewSection } from '../components/cockpit/BeraterDimensionReviewSection';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ErrorPanel } from '../components/ui/ErrorPanel';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { STATUS_BADGE_STYLES, STATUS_LABELS } from './Cases';

type GenerateState =
  | { kind: 'idle' }
  | { kind: 'rendering' }
  | { kind: 'success'; filename: string; mode: 'demo' | 'live' }
  | { kind: 'pdf-opened'; mode: 'demo' | 'live' }
  | { kind: 'pdf-blocked'; hint: string }
  | { kind: 'error'; title?: string; messages: readonly string[]; footnote?: string };

type OutputFormat = 'docx' | 'pdf';

/**
 * Berater-Cockpit (AP6 / AP8 / AP10 / AP14).
 *
 * AP14: Stellungnahme nutzt echte Mandanten-Stammdaten aus der DB
 * (`buildInputFromCase`), wenn der User in Production eingeloggt ist
 * UND die Case-Pflichtfelder gesetzt sind. Im Dev-Mock-Pfad oder bei
 * unvollständigen Stammdaten greift weiterhin die Demo-Fixture.
 *
 * Bewertungstexte (assessments): siehe AP10 — wenn alle Pflichttexte im
 * Editor vollständig sind, fließen sie ein; sonst Demo-Texte.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type ReviewWorkflowState =
  | { kind: 'idle' }
  | { kind: 'requesting' }
  | { kind: 'requested' }
  | { kind: 'error'; message: string };

export function Cockpit() {
  const { id: caseId = 'demo' } = useParams<{ id?: string }>();
  const auth = useAuthOrSession();
  const { isMock } = auth;
  const confirm = useConfirm();
  // Berater-Bearbeitung (Dimensionen + Assessments) in die Berater-Ebene
  // synchronisieren. Im Mock/Demo bleibt der Hook idle (userId null).
  useWizardSync(caseId, {
    userId: isMock ? null : (auth.user?.id ?? null),
    layer: 'berater',
  });
  const draft = useWizardStore((s) => s.cases[caseId]?.assessments);
  const ensureCase = useWizardStore((s) => s.ensureCase);
  const [state, setState] = useState<GenerateState>({ kind: 'idle' });
  const [caseStatus, setCaseStatus] = useState<CaseStatus | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [openReview, setOpenReview] = useState<CaseReviewRow | null>(null);
  const [reviewWorkflow, setReviewWorkflow] = useState<ReviewWorkflowState>({ kind: 'idle' });
  const [versions, setVersions] = useState<StatementVersionRow[]>([]);
  const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);
  // C3: Hat ein Mandant aktiven Zugang zu diesem Case? Steuert die
  // Sichtbarkeit des MandantenStatusBoard.
  const [hasMandantAccess, setHasMandantAccess] = useState(false);

  // Production: caseId muss UUID sein. Demo-Pfad ('demo') ist nur im
  // Dev-Mock erlaubt. Alle anderen Werte (Tippfehler, Platzhalter aus
  // Doku, `<uuid>`-Strings) sind ungültig.
  const isInvalidCaseId = !isMock && caseId !== 'demo' && !UUID_RE.test(caseId);

  if (isInvalidCaseId) {
    return <InvalidCaseIdScreen caseId={caseId} />;
  }

  if (!draft) {
    ensureCase(caseId);
  }

  const readiness = draft
    ? checkAssessmentReadiness(draft)
    : { isReady: false, missingScalars: [], missingDimensionDetails: [] };
  const customAssessments = draft && readiness.isReady ? selectAssessmentsForCase(draft) : null;

  const isLiveCase = !isMock && caseId !== 'demo';

  // Case-Status + offenen Review nachladen, damit der Generate-Button
  // korrekt gesperrt wird (nur 'completed' = nach Review-Approval erlaubt).
  // Im Demo-/Mock-Pfad überspringen.
  useEffect(() => {
    if (!isLiveCase) {
      setCaseStatus('in_progress');
      setOpenReview(null);
      return;
    }
    void Promise.all([getCase(caseId), getOpenReviewForCase(caseId)])
      .then(([caseRow, review]) => {
        setCaseStatus(caseRow?.status ?? null);
        setClientName(caseRow?.client_full_name ?? null);
        setOpenReview(review);
      })
      .catch((err: unknown) => {
        console.error('Cockpit: case-status load failed', err);
      });
  }, [isLiveCase, caseId, reviewWorkflow.kind]);

  // C3: Mandanten-Access-Status prüfen (für die Sichtbarkeit des
  // Status-Boards). Reagiert auf reviewWorkflow.kind nicht — Berater-
  // Aktionen am Review beeinflussen den Mandanten-Zugang nicht.
  useEffect(() => {
    if (!isLiveCase) {
      setHasMandantAccess(false);
      return;
    }
    void listMandantAccessForCase(caseId)
      .then((rows) => {
        setHasMandantAccess(rows.some((a) => a.revoked_at === null));
      })
      .catch((err: unknown) => {
        console.warn('Cockpit: listMandantAccessForCase failed', err);
        setHasMandantAccess(false);
      });
  }, [isLiveCase, caseId]);

  async function handleRequestReview(): Promise<void> {
    if (!isLiveCase) {
      setReviewWorkflow({ kind: 'error', message: de.cockpitErrors.demoFourEyes });
      return;
    }
    if (!customAssessments) {
      setReviewWorkflow({ kind: 'error', message: de.cockpitErrors.assessmentsIncomplete });
      return;
    }
    setReviewWorkflow({ kind: 'requesting' });
    try {
      await requestReview(caseId);
      setReviewWorkflow({ kind: 'requested' });
    } catch (err) {
      setReviewWorkflow({
        kind: 'error',
        message: err instanceof Error ? err.message : de.cockpitErrors.reviewRequestFailed,
      });
    }
  }

  // Vier-Augen-Prinzip — Admin-toggable seit Migration 0027.
  // Wenn Flag aus → Generate-Button immer verfügbar (Test-Modus, dokumentiert
  // im audit_log durch das Feature-Flag-Trigger).
  const [fourEyesRequired, setFourEyesRequired] = useState<boolean>(true);
  useEffect(() => {
    let cancelled = false;
    void getFeatureFlag('four_eyes_required')
      .then((enabled) => {
        if (!cancelled) setFourEyesRequired(enabled);
      })
      .catch(() => {
        // Fail-closed: bei DB-Fehler bleibt Vier-Augen Pflicht
        if (!cancelled) setFourEyesRequired(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Verbindliche Berater-Verantwortung (Art. 22 DSGVO / CLAUDE.md Regel 6):
  // vor jeder Live-Generierung muss der Berater explizit bestätigen, dass er
  // die Einordnung fachlich verantwortet — der Score ist nur Vorbereitung.
  const [responsibilityAccepted, setResponsibilityAccepted] = useState(false);

  // Stellungnahme generieren ist erst erlaubt, wenn:
  //  - im Demo/Mock immer
  //  - in Production: Case-Status === 'completed' (= Reviewer hat freigegeben)
  //    ODER Vier-Augen-Pflicht ist im System deaktiviert
  const reviewCleared = !isLiveCase || caseStatus === 'completed' || !fourEyesRequired;
  // Für Live-Cases zusätzlich die explizite Verantwortungs-Bestätigung.
  const canGenerate = reviewCleared && (!isLiveCase || responsibilityAccepted);

  // C2: Versions-History laden — nur Live-Cases haben Einträge.
  useEffect(() => {
    if (!isLiveCase) {
      setVersions([]);
      return;
    }
    void listStatementVersionsForCase(caseId)
      .then((rows) => {
        setVersions(rows);
      })
      .catch((err: unknown) => {
        console.warn('Cockpit: listStatementVersionsForCase failed', err);
        setVersions([]);
      });
  }, [isLiveCase, caseId, versionsRefreshKey]);

  /**
   * C2: persistiert eine neu generierte Stellungnahme als append-only
   * Version. Schluckt Fehler bewusst (logged nur), damit ein DB-Hänger
   * den User-Download nicht blockiert — die DOCX/PDF ist schon gerendert.
   */
  async function logVersion(
    format: OutputFormat,
    placeholders: statement.StatementPlaceholders,
    input: statement.BuildPlaceholdersInput,
  ): Promise<void> {
    try {
      await createStatementVersion({
        caseId,
        format,
        scoreValue: input.score.totalScore,
        scoreConfidence: input.score.totalConfidencePercent,
        trafficLight: input.score.trafficLight,
        verdict: placeholders['VERDICT'] ?? '',
        placeholders,
      });
      setVersionsRefreshKey((k) => k + 1);
    } catch (err) {
      console.warn('Cockpit: createStatementVersion failed (non-blocking)', err);
    }
  }

  /**
   * Erzeugt entweder DOCX-Download oder Print-Vorschau (PDF).
   *
   * Beide Formate teilen sich denselben Daten-Pfad bis zu den Platzhaltern;
   * erst danach trennen sich die Wege:
   *   - 'docx': renderAndDownloadStatement (docxtemplater + Browser-Download)
   *   - 'pdf':  openPrintPreview (HTML-Render in neuem Tab + Browser-Print)
   */
  async function handleGenerate(format: OutputFormat): Promise<void> {
    // Defense-in-depth: auch wenn der Button-Disabled-State umgangen würde,
    // blockiert die Generierung ohne Reviewer-Freigabe bzw. ohne explizite
    // Verantwortungs-Bestätigung des Beraters.
    if (isLiveCase && !canGenerate) {
      setState({
        kind: 'error',
        messages: [
          !reviewCleared
            ? 'Die Stellungnahme darf erst nach Freigabe durch die zweite Bewertung generiert werden.'
            : de.cockpitResponsibility.hintUnchecked,
        ],
      });
      return;
    }
    setState({ kind: 'rendering' });
    try {
      // Pfad 1: Live-Case in Production → buildInputFromCase
      if (isLiveCase) {
        // Live-Wizard-State (beide Ebenen, entschlüsselt) für Score + Finanzen.
        const wizardCase = useWizardStore.getState().cases[caseId];
        const result = await buildInputFromCase(caseId, customAssessments, wizardCase);
        if (result.status === 'not_found') {
          setState({ kind: 'error', messages: [de.cockpitErrors.caseNotFound] });
          return;
        }
        if (result.status === 'incomplete') {
          setState({
            kind: 'error',
            title: de.cockpitErrors.missingDataTitle,
            messages: result.missing,
            footnote: de.cockpitErrors.missingDataFootnote,
          });
          return;
        }
        const issues = statement.validateStatementInput(result.input);
        const blocking = statement.getBlockingIssues(issues);
        if (blocking.length > 0) {
          setState({
            kind: 'error',
            title: de.cockpitErrors.validationTitle.replace('{count}', String(blocking.length)),
            messages: blocking.map((i) => i.message),
          });
          return;
        }
        // Konfidenz-Vorbehalt: bei < 60 % Bewertungsgrundlage zu dünn für eine
        // formelle Stellungnahme nach FW-BA-Standard. Bewusste Confirm-Sperre,
        // damit der Berater die Nachforderung von Unterlagen aktiv abwägt.
        const confidence = result.input.score.totalConfidencePercent as number;
        if (confidence < 60) {
          const proceed = await confirm({
            title: de.cockpitConfidenceConfirm.title,
            body: `${de.cockpitConfidenceConfirm.body.replace('{confidence}', String(confidence))}\n\n${de.cockpitConfidenceConfirm.question}`,
            confirmLabel: de.cockpitConfidenceConfirm.confirmLabel,
            cancelLabel: de.cockpitConfidenceConfirm.cancelLabel,
          });
          if (!proceed) {
            setState({
              kind: 'error',
              messages: [
                de.cockpitErrors.confidenceAborted.replace('{confidence}', String(confidence)),
              ],
            });
            return;
          }
        }
        const placeholders = statement.buildStatementPlaceholders(result.input);
        if (format === 'docx') {
          await renderAndDownloadStatement(placeholders);
          const filename = (placeholders['CASE_ID'] ?? 'Stellungnahme') + '.docx';
          setState({ kind: 'success', filename, mode: 'live' });
        } else {
          const r = openPrintPreview(placeholders);
          setState(
            r.status === 'opened'
              ? { kind: 'pdf-opened', mode: 'live' }
              : { kind: 'pdf-blocked', hint: r.hint ?? de.cockpitErrors.popupBlocked },
          );
          if (r.status === 'blocked') return;
        }
        // C2: append-only Versionierung. Nur in Production loggen — im
        // Demo-Pfad würden wir Demo-Daten in die DB schreiben.
        await logVersion(format, placeholders, result.input);
        return;
      }

      // Pfad 2: Demo-Case (Dev-Mock oder Demo-UUID)
      const baseInput = buildDemoStatementInput();
      const input = customAssessments
        ? { ...baseInput, assessments: customAssessments }
        : baseInput;
      const issues = statement.validateStatementInput(input);
      const blocking = statement.getBlockingIssues(issues);
      if (blocking.length > 0) {
        setState({
          kind: 'error',
          title: de.cockpitErrors.validationTitle.replace('{count}', String(blocking.length)),
          messages: blocking.map((i) => i.message),
        });
        return;
      }
      const placeholders = statement.buildStatementPlaceholders(input);
      if (format === 'docx') {
        await renderAndDownloadStatement(placeholders);
        const filename = (placeholders['CASE_ID'] ?? 'Stellungnahme') + '.docx';
        setState({ kind: 'success', filename, mode: 'demo' });
      } else {
        const r = openPrintPreview(placeholders);
        setState(
          r.status === 'opened'
            ? { kind: 'pdf-opened', mode: 'demo' }
            : { kind: 'pdf-blocked', hint: r.hint ?? de.cockpitErrors.popupBlocked },
        );
      }
    } catch (err) {
      setState({
        kind: 'error',
        messages: [err instanceof Error ? err.message : de.cockpitErrors.generateUnknown],
      });
    }
  }

  const generatorHint = (() => {
    if (isLiveCase && customAssessments) {
      return 'Stellungnahme nutzt echte Mandanten-Stammdaten aus der Datenbank UND Ihre eigenen Bewertungstexte. Die wirtschaftlichen Kennzahlen stammen noch aus Beispieldaten, solange die Finanzerfassung nicht vollständig übernommen ist.';
    }
    if (isLiveCase) {
      return 'Stellungnahme nutzt echte Mandanten-Stammdaten aus der Datenbank. Ihre fachlichen Bewertungstexte sind noch nicht vollständig — bitte pflegen Sie sie unten im Editor, bevor Sie generieren.';
    }
    if (customAssessments) {
      return 'Demo-Modus mit Ihren eigenen Bewertungstexten. Sobald Sie ein echtes Mandat über „/cases" anlegen, werden Stammdaten aus der DB gezogen.';
    }
    return 'Demo-Modus: Generator nutzt komplett synthetische Beispieldaten (Frau Mustermann). Pflegen Sie unten Ihre Texte oder legen Sie ein echtes Mandat an.';
  })();

  return (
    <div className="pb-24 pt-12 md:pt-20">
      <Link
        to={`/case/${caseId}/hub`}
        className="mb-6 inline-flex items-center gap-1.5 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 hover:text-stone-900"
      >
        <ArrowLeft size={14} />
        {de.nav.backToHub}
      </Link>

      <header className="relative pb-10">
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 hidden h-24 w-24 rounded-sm md:block"
          style={{ backgroundColor: 'var(--color-dim-resources)', opacity: 0.18 }}
        />
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.cockpit.kicker}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <h1 className="font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-6xl">
            {de.cockpit.title}
          </h1>
          {caseStatus && (
            <span
              className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${STATUS_BADGE_STYLES[caseStatus]}`}
            >
              {STATUS_LABELS[caseStatus]}
            </span>
          )}
        </div>
        <p className="mt-3 font-serif text-base italic text-stone-500 md:text-lg">
          {de.cockpit.subtitle}
        </p>
        {clientName && (
          <p className="mt-4 font-sans text-xs font-medium uppercase tracking-widest text-stone-400">
            {de.cockpit.caseContextLabel}: <span className="text-stone-700">{clientName}</span>
          </p>
        )}
      </header>

      {/* C3: Mandanten-Self-Service — Einladung + Status-Board (nur Live) */}
      {isLiveCase && <InviteMandantSection caseId={caseId} />}
      {isLiveCase && <MandantenStatusBoard caseId={caseId} hasActiveAccess={hasMandantAccess} />}

      {/* Phase A: Stammdaten + Dokumenten-Übersicht.
          Phase B: auch im Demo-Modus sichtbar — die Komponente erkennt den
          Demo-Case und zeigt die Mock-Fixtures (siehe demoFixtures.ts). */}
      {(isLiveCase || caseId === 'demo') && (
        <>
          {!isLiveCase && (
            <DemoModeBanner hint="Demo-Modus — Stammdaten, Dokumente und Rückfragen sind synthetisch und werden nicht persistiert." />
          )}
          <CockpitStammdatenAndDocs caseId={caseId} />
          <BeraterDimensionReviewSection caseId={caseId} />
          <ClarificationsSection caseId={caseId} />
          <DsgvoSection caseId={caseId} />
        </>
      )}

      {/* Vier-Augen-Prinzip — nur sichtbar wenn Live + Flag aktiv */}
      {isLiveCase && fourEyesRequired && (
        <section className="mb-10 rounded-sm border border-stone-200 bg-white">
          <div className="border-b border-stone-200 px-6 py-5 md:px-10">
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
              Vier-Augen-Prinzip
            </p>
            <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
              ZWEITE BEWERTUNG.
            </h2>
            <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-500">
              Vor der Generierung muss eine zweite, beratende Person den Case freigeben. Sie
              bestätigt formale Plausibilität und fachliche Eindeutigkeit der Bewertungstexte.
            </p>
          </div>

          <div className="px-6 py-6 md:px-10">
            {caseStatus === 'completed' ? (
              <div className="flex items-start gap-3 rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3">
                <ShieldCheck size={18} className="mt-0.5 flex-shrink-0 text-emerald-700" />
                <div>
                  <p className="font-sans text-xs font-medium uppercase tracking-widest text-emerald-800">
                    Freigegeben
                  </p>
                  <p className="mt-1 font-serif text-sm text-emerald-900">
                    Eine zweite Berater:in hat den Case geprüft und freigegeben. Die Stellungnahme
                    kann jetzt generiert werden.
                  </p>
                </div>
              </div>
            ) : openReview || caseStatus === 'review' ? (
              <div className="flex items-start gap-3 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3">
                <Lock size={18} className="mt-0.5 flex-shrink-0 text-amber-700" />
                <div className="flex-1">
                  <p className="font-sans text-xs font-medium uppercase tracking-widest text-amber-800">
                    In Prüfung
                  </p>
                  <p className="mt-1 font-serif text-sm text-amber-900">
                    Der Case liegt zur zweiten Bewertung vor. Sobald eine Berater:in im selben
                    Tenant freigibt, wird der Generator entsperrt.
                  </p>
                  {openReview && (
                    <p className="mt-2 font-serif text-xs italic text-amber-800">
                      Angefordert am {new Date(openReview.requested_at).toLocaleString('de-DE')}.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="max-w-prose font-serif text-sm text-stone-700">
                  Wenn die Bewertungstexte vollständig sind, geben Sie den Case zur kollegialen
                  Prüfung frei.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    void handleRequestReview();
                  }}
                  disabled={reviewWorkflow.kind === 'requesting' || !customAssessments}
                  className="flex-shrink-0"
                >
                  {reviewWorkflow.kind === 'requesting'
                    ? 'Wird freigegeben…'
                    : 'Zur zweiten Bewertung freigeben →'}
                </Button>
              </div>
            )}

            {reviewWorkflow.kind === 'error' && (
              <div className="mt-4">
                <ErrorPanel messages={[reviewWorkflow.message]} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Generate-Sektion */}
      <section className="mb-10 rounded-sm border border-stone-200 bg-white">
        <div className="border-b border-stone-200 px-6 py-5 md:px-10">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            Stellungnahme
          </p>
          <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
            STELLUNGNAHME GENERIEREN.
          </h2>
          <p className="mt-2 font-serif text-sm italic text-stone-500">{generatorHint}</p>
          {isLiveCase && !reviewCleared && (
            <p className="mt-3 inline-flex items-center gap-2 rounded-sm bg-stone-100 px-3 py-1.5 font-sans text-xs uppercase tracking-widest text-stone-700">
              <Lock size={12} />
              Erst nach Freigabe durch zweite Bewertung
            </p>
          )}
        </div>

        <div className="px-6 py-6 md:px-10">
          {isLiveCase && reviewCleared && (
            <label className="mb-5 flex cursor-pointer items-start gap-3 rounded-sm border border-stone-200 bg-stone-50 px-4 py-3">
              <input
                type="checkbox"
                checked={responsibilityAccepted}
                onChange={(e) => {
                  setResponsibilityAccepted(e.target.checked);
                }}
                className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-sm border-stone-400 text-stone-900 focus:ring-2 focus:ring-stone-900 focus:ring-offset-1"
              />
              <span className="font-serif text-sm leading-relaxed text-stone-700">
                {de.cockpitResponsibility.label}
              </span>
            </label>
          )}
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => {
                void handleGenerate('docx');
              }}
              disabled={state.kind === 'rendering' || !canGenerate}
              className="flex-1 rounded-sm bg-stone-900 px-6 py-4 font-sans text-sm font-medium uppercase tracking-widest text-white transition-colors hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-400"
              title={
                !reviewCleared
                  ? 'Erst nach Freigabe durch zweite Bewertung verfügbar'
                  : !canGenerate
                    ? de.cockpitResponsibility.hintUnchecked
                    : undefined
              }
            >
              {state.kind === 'rendering'
                ? 'Generiere…'
                : isLiveCase
                  ? 'Als DOCX herunterladen →'
                  : 'Demo: DOCX herunterladen →'}
            </button>
            <button
              type="button"
              onClick={() => {
                void handleGenerate('pdf');
              }}
              disabled={state.kind === 'rendering' || !canGenerate}
              className="flex-1 rounded-sm border border-stone-900 bg-white px-6 py-4 font-sans text-sm font-medium uppercase tracking-widest text-stone-900 transition-colors hover:bg-stone-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              title={
                !reviewCleared
                  ? 'Erst nach Freigabe durch zweite Bewertung verfügbar'
                  : !canGenerate
                    ? de.cockpitResponsibility.hintUnchecked
                    : 'Öffnet eine druckbare Vorschau in einem neuen Tab. Im Druck-Dialog „Als PDF speichern" wählen.'
              }
            >
              {isLiveCase ? 'Vorschau & PDF drucken →' : 'Demo: Vorschau & PDF →'}
            </button>
          </div>

          <p className="mt-3 font-serif text-xs italic text-stone-500">
            DOCX für Word-Bearbeitung. PDF wird im Browser gedruckt — keine Cloud-Konvertierung,
            Daten bleiben auf Ihrem Gerät.
          </p>

          {state.kind === 'success' && (
            <p className="mt-4 font-sans text-sm text-emerald-700">
              ✓ {state.filename} wurde heruntergeladen
              {state.mode === 'live' ? ' (mit echten Mandanten-Stammdaten).' : ' (Demo-Modus).'}
            </p>
          )}
          {state.kind === 'pdf-opened' && (
            <p className="mt-4 font-sans text-sm text-emerald-700">
              ✓ Vorschau in neuem Tab geöffnet
              {state.mode === 'live'
                ? ' (mit echten Mandanten-Stammdaten).'
                : ' (Demo-Modus).'}{' '}
              Dort den Druck-Button klicken und „Als PDF speichern" wählen.
            </p>
          )}
          {state.kind === 'pdf-blocked' && (
            <div className="mt-4">
              <ErrorPanel tone="warning" messages={[state.hint]} />
            </div>
          )}
          {state.kind === 'error' && (
            <div className="mt-4">
              <ErrorPanel title={state.title} messages={state.messages} footnote={state.footnote} />
            </div>
          )}
        </div>
      </section>

      {/* C2: Statement-Versions-History (nur in Production sichtbar) */}
      {isLiveCase && versions.length > 0 && (
        <section className="mb-10 rounded-sm border border-stone-200 bg-white">
          <div className="border-b border-stone-200 px-6 py-5 md:px-10">
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
              Beweisspur · Append-only
            </p>
            <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
              GENERIERTE STELLUNGNAHMEN.
            </h2>
            <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-500">
              Jede Generierung wird mit Zeitstempel, Berater, Score-Snapshot und vollständigen
              Platzhalter-Daten protokolliert. Diese Liste lässt sich nicht nachträglich ändern oder
              löschen — Aufbewahrungsfrist 10 Jahre nach DSGVO-Löschkonzept.
            </p>
          </div>
          <ul className="divide-y divide-stone-200">
            {versions.map((v) => (
              <li
                key={v.id}
                className="flex flex-col gap-2 px-6 py-4 sm:flex-row sm:items-center sm:justify-between md:px-10"
              >
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      tone="info"
                      className={
                        v.format === 'docx' ? 'border-0 bg-stone-900 text-white' : undefined
                      }
                    >
                      {v.format.toUpperCase()}
                    </Badge>
                    <Badge
                      tone={
                        v.traffic_light === 'green'
                          ? 'success'
                          : v.traffic_light === 'yellow'
                            ? 'warning'
                            : 'danger'
                      }
                    >
                      Score {v.score_value} · {v.traffic_light}
                    </Badge>
                    <span className="font-sans text-[10px] uppercase tracking-widest text-stone-500">
                      Konfidenz {v.score_confidence} %
                    </span>
                  </div>
                  <p className="mt-1 font-serif text-sm text-stone-900">{v.verdict}</p>
                  <p className="mt-0.5 font-serif text-xs italic text-stone-500">
                    {new Date(v.generated_at).toLocaleString('de-DE')}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* AssessmentEditor */}
      <section className="rounded-sm border border-stone-200 bg-white px-6 py-8 md:px-10 md:py-10">
        <AssessmentEditor caseId={caseId} />
      </section>
    </div>
  );
}

/**
 * Phase B: Demo-Modus-Banner über den Phase-A-Sektionen, damit der Berater
 * sofort versteht, dass die angezeigten Stammdaten/Dokumente/Rückfragen
 * synthetisch sind. Edits/Uploads in den Sektionen sind im Demo-Modus
 * no-ops (siehe `isMock`-Gates in den jeweiligen Komponenten).
 */
function DemoModeBanner({ hint }: { readonly hint: string }) {
  return (
    <div
      role="status"
      className="mb-6 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 font-sans text-xs font-medium uppercase tracking-widest text-amber-900"
    >
      <span className="not-italic">Demo-Modus · </span>
      <span className="font-serif text-sm normal-case tracking-normal italic">{hint}</span>
    </div>
  );
}

interface InvalidCaseIdProps {
  caseId: string;
}

function InvalidCaseIdScreen({ caseId }: InvalidCaseIdProps) {
  const navigate = useNavigate();
  return (
    <div className="mx-auto max-w-prose pb-24 pt-16 md:pt-24">
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        Beratung
      </p>
      <h1 className="mt-3 font-serif text-4xl font-black leading-tight tracking-tight text-stone-900 md:text-5xl">
        UNGÜLTIGER PFAD.
      </h1>
      <p className="mt-4 font-serif text-base italic text-stone-500">
        Die URL enthält keine gültige Mandats-ID. Vermutlich wurde ein Platzhalter aus einer
        Anleitung wörtlich übernommen — bitte über die Mandantenliste zum gewünschten Case
        navigieren.
      </p>
      <pre className="mt-4 inline-block rounded-sm bg-stone-100 px-3 py-1 font-mono text-xs text-stone-700">
        {caseId}
      </pre>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <Button
          type="button"
          onClick={() => {
            void navigate('/cases');
          }}
        >
          Zur Mandantenliste →
        </Button>
      </div>
    </div>
  );
}
